/**
 * Sign-in to the admin portal: Cognito's managed login with the authorization code flow and PKCE
 * (RFC 7636), on the portal's own public app client in the same user pool as the daemon. The id
 * token is what the data API takes. Tokens live in the tab's `sessionStorage`; closing the tab
 * signs out.
 */

export type Role = "operator" | "admin" | "member";
const ROLES: readonly Role[] = ["operator", "admin", "member"];

export interface Tokens {
  readonly idToken: string;
  readonly refreshToken?: string;
  /** When the id token expires, in ms since the epoch. */
  readonly expiresAt: number;
}

export interface Identity {
  readonly email: string | undefined;
  /** The caller's highest role group; undefined when it has none. */
  readonly role: Role | undefined;
}

export interface AuthOptions {
  readonly authDomain: string;
  readonly clientId: string;
  /** The portal's root, as registered for the client: `<origin>/`. */
  readonly redirectUri: string;
  readonly storage?: Storage;
  readonly fetch?: typeof fetch;
  readonly now?: () => number;
}

const TOKENS_KEY = "iqa-admin-tokens";
const PENDING_KEY = "iqa-admin-pending";
/** Renew this long before the id token expires. */
const RENEW_MS = 60_000;
/** The longest a token request may take. */
const REQUEST_MS = 10_000;

/** The claims of a JWT, unverified: the data API verifies the token; the portal only reads it. */
export function claimsOf(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  if (!payload) return {};
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")), (c) =>
        c.charCodeAt(0),
      ),
    );
    const claims = JSON.parse(json) as unknown;
    return typeof claims === "object" && claims !== null ? (claims as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Who a token is: its email and highest role group. */
export function identityOf(token: string): Identity {
  const claims = claimsOf(token);
  const groups = Array.isArray(claims["cognito:groups"]) ? claims["cognito:groups"] : [];
  return {
    email: typeof claims.email === "string" ? claims.email : undefined,
    role: ROLES.find((role) => groups.includes(role)),
  };
}

function base64url(bytes: Uint8Array): string {
  let text = "";
  for (const byte of bytes) text += String.fromCharCode(byte);
  return btoa(text).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomText(bytes = 32): string {
  return base64url(crypto.getRandomValues(new Uint8Array(bytes)));
}

/** The S256 code challenge of a verifier. */
export async function challengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export class Auth {
  readonly #options: AuthOptions;
  readonly #storage: Storage;
  readonly #fetch: typeof fetch;
  readonly #now: () => number;
  #renewing: Promise<Tokens | undefined> | undefined;

  constructor(options: AuthOptions) {
    this.#options = options;
    this.#storage = options.storage ?? sessionStorage;
    this.#fetch = options.fetch ?? ((...args) => fetch(...args));
    this.#now = options.now ?? Date.now;
  }

  /** The URL of Cognito's sign-in page; remembers the verifier and state for the return. */
  async signInUrl(): Promise<string> {
    const verifier = randomText(48);
    const state = randomText(16);
    this.#storage.setItem(PENDING_KEY, JSON.stringify({ verifier, state }));
    const query = new URLSearchParams({
      response_type: "code",
      client_id: this.#options.clientId,
      redirect_uri: this.#options.redirectUri,
      scope: "openid email profile",
      state,
      code_challenge: await challengeOf(verifier),
      code_challenge_method: "S256",
    });
    return `${this.#options.authDomain}/oauth2/authorize?${query}`;
  }

  /**
   * Finishes a sign-in when the URL is Cognito's redirect back (`?code=…&state=…`): checks the state,
   * exchanges the code with the verifier. Returns false when the URL is not a redirect back.
   */
  async completeSignIn(url: URL): Promise<boolean> {
    const code = url.searchParams.get("code");
    const error = url.searchParams.get("error");
    if (!code && !error) return false;
    const pending = this.#storage.getItem(PENDING_KEY);
    this.#storage.removeItem(PENDING_KEY);
    if (error)
      throw new Error(`Sign-in failed: ${url.searchParams.get("error_description") ?? error}`);
    const { verifier, state } = pending
      ? (JSON.parse(pending) as { verifier?: string; state?: string })
      : {};
    if (!verifier || state !== url.searchParams.get("state")) {
      throw new Error("Sign-in failed: this sign-in was not started here. Sign in again");
    }
    const tokens = await this.#token({
      grant_type: "authorization_code",
      code: code as string,
      redirect_uri: this.#options.redirectUri,
      code_verifier: verifier,
    });
    this.#save(tokens);
    return true;
  }

  /** The id token for the data API, renewed with the refresh token near its expiry. */
  async idToken(): Promise<string | undefined> {
    const tokens = this.#load();
    if (!tokens) return undefined;
    if (tokens.expiresAt - RENEW_MS > this.#now()) return tokens.idToken;
    if (!tokens.refreshToken) {
      this.#storage.removeItem(TOKENS_KEY);
      return undefined;
    }
    this.#renewing ??= this.#renew(tokens.refreshToken).finally(() => {
      this.#renewing = undefined;
    });
    return (await this.#renewing)?.idToken;
  }

  /** Forgets the tokens; returns Cognito's sign-out URL, which ends its session too. */
  signOutUrl(): string {
    this.#storage.removeItem(TOKENS_KEY);
    const query = new URLSearchParams({
      client_id: this.#options.clientId,
      logout_uri: this.#options.redirectUri,
    });
    return `${this.#options.authDomain}/logout?${query}`;
  }

  async #renew(refreshToken: string): Promise<Tokens | undefined> {
    try {
      const tokens = await this.#token({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      });
      const renewed = { ...tokens, refreshToken: tokens.refreshToken ?? refreshToken };
      this.#save(renewed);
      return renewed;
    } catch {
      // Expired or revoked (a disabled account): sign in again.
      this.#storage.removeItem(TOKENS_KEY);
      return undefined;
    }
  }

  async #token(params: Record<string, string>): Promise<Tokens> {
    const response = await this.#fetch(`${this.#options.authDomain}/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ client_id: this.#options.clientId, ...params }),
      signal: AbortSignal.timeout(REQUEST_MS),
    });
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok || typeof body.id_token !== "string") {
      throw new Error(`Sign-in failed: ${String(body.error ?? `HTTP ${response.status}`)}`);
    }
    const expiresIn = typeof body.expires_in === "number" ? body.expires_in : 3600;
    return {
      idToken: body.id_token,
      ...(typeof body.refresh_token === "string" && { refreshToken: body.refresh_token }),
      expiresAt: this.#now() + expiresIn * 1000,
    };
  }

  #load(): Tokens | undefined {
    try {
      const tokens = JSON.parse(this.#storage.getItem(TOKENS_KEY) ?? "null") as Tokens | null;
      return tokens && typeof tokens.idToken === "string" ? tokens : undefined;
    } catch {
      return undefined;
    }
  }

  #save(tokens: Tokens): void {
    this.#storage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  }
}
